import { useCallback, useRef, useState } from 'react';
import { auth } from '../firebase';
import {
  deliveriesReportService,
  DeliveriesReportRow,
  DeliveriesReportProductColumn,
  GetDeliveriesReportRequest,
} from '../services/firebaseFunctions';
import {
  ReportDimension,
  triggerBlobDownload,
  buildCsvFilename,
} from '../utils/deliveriesReportUtils';

// ---------------------------------------------------------------------------
// Tipos internos
// ---------------------------------------------------------------------------

export interface ReportFilters {
  companyId: string;
  publisherId: string;
  productId: string;
  comboId: string;
}

export interface ReportPagination {
  totalRows: number;
  totalPages: number;
}

const EMPTY_FILTERS: ReportFilters = {
  companyId: '',
  publisherId: '',
  productId: '',
  comboId: '',
};

// ---------------------------------------------------------------------------
// Interface pública do hook
// ---------------------------------------------------------------------------

export interface UseDeliveriesReportReturn {
  // Parâmetros do relatório
  dimension: ReportDimension;
  dateStart: string;
  dateEnd: string;
  filters: ReportFilters;

  // Paginação server-side
  page: number;
  pageSize: number;
  pagination: ReportPagination | null;

  // Resultado (apenas a página atual)
  rows: DeliveriesReportRow[];
  summary: { totalDeliveries: number } | null;
  /** Somente para dimension:'combo' — colunas de produto globais (todas as páginas). */
  productColumns: DeliveriesReportProductColumn[];

  // Estado de UI
  loading: boolean;
  exporting: boolean;
  error: string | null;
  hasGenerated: boolean;

  // Ações de configuração (NÃO disparam fetch automaticamente)
  setDimension: (dim: ReportDimension) => void;
  setDateStart: (date: string) => void;
  setDateEnd: (date: string) => void;
  setFilter: (key: keyof ReportFilters, value: string) => void;
  clearFilters: () => void;

  // Ações de fetch
  generateReport: () => Promise<void>;

  // Ações de paginação (DISPARAM fetch automaticamente)
  setPage: (newPage: number) => void;
  setPageSize: (newPageSize: number) => void;

  // Exportação
  exportCsv: () => Promise<void>;
}

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

export function useDeliveriesReport(): UseDeliveriesReportReturn {
  // Parâmetros do relatório
  const [dimension, setDimensionState] = useState<ReportDimension>('company');
  const [dateStart, setDateStartState] = useState<string>('');
  const [dateEnd, setDateEndState] = useState<string>('');
  const [filters, setFiltersState] = useState<ReportFilters>({ ...EMPTY_FILTERS });

  // Paginação
  const [page, setPageState] = useState<number>(1);
  const [pageSize, setPageSizeState] = useState<number>(50);
  const [pagination, setPaginationState] = useState<ReportPagination | null>(null);

  // Resultado
  const [rows, setRows] = useState<DeliveriesReportRow[]>([]);
  const [summary, setSummary] = useState<{ totalDeliveries: number } | null>(null);
  const [productColumns, setProductColumns] = useState<DeliveriesReportProductColumn[]>([]);

  // Estado de UI
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [hasGenerated, setHasGenerated] = useState(false);

  // Refs para evitar stale closures nas chamadas assíncronas.
  // Atualizados no corpo do hook (a cada render) para refletir o estado atual.
  const dimensionRef = useRef(dimension);
  const dateStartRef = useRef(dateStart);
  const dateEndRef = useRef(dateEnd);
  const filtersRef = useRef(filters);
  const pageSizeRef = useRef(pageSize);

  dimensionRef.current = dimension;
  dateStartRef.current = dateStart;
  dateEndRef.current = dateEnd;
  filtersRef.current = filters;
  pageSizeRef.current = pageSize;

  // ---------------------------------------------------------------------------
  // Helpers internos
  // ---------------------------------------------------------------------------

  const clearResult = useCallback(() => {
    setRows([]);
    setPaginationState(null);
    setSummary(null);
    setProductColumns([]);
    setHasGenerated(false);
  }, []);

  /** Constrói o objeto de filtros omitindo entradas vazias. */
  const buildFiltersParam = (): GetDeliveriesReportRequest['filters'] => {
    const f = filtersRef.current;
    const param: NonNullable<GetDeliveriesReportRequest['filters']> = {};
    if (f.companyId) param.companyId = f.companyId;
    if (f.publisherId) param.publisherId = f.publisherId;
    if (f.productId) param.productId = f.productId;
    if (f.comboId) param.comboId = f.comboId;
    return Object.keys(param).length > 0 ? param : undefined;
  };

  // ---------------------------------------------------------------------------
  // Fetch interno — lê dimensão/datas/filtros de refs; recebe page/pageSize explicitamente
  // ---------------------------------------------------------------------------

  const doFetch = useCallback(async (pg: number, ps: number): Promise<void> => {
    const ds = dateStartRef.current;
    const de = dateEndRef.current;

    // Validação antes de chamar o backend
    if (!ds || !de) {
      setError('Selecione um período válido');
      return;
    }
    if (de < ds) {
      setError('Data final deve ser posterior à data inicial');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const response = await deliveriesReportService.getDeliveriesReport({
        dimension: dimensionRef.current,
        dateStart: ds,
        dateEnd: de,
        filters: buildFiltersParam(),
        page: pg,
        pageSize: ps,
      });

      setRows(response.data.rows);
      setPaginationState({
        totalRows: response.data.pagination.totalRows,
        totalPages: response.data.pagination.totalPages,
      });
      setSummary(response.data.summary);
      setProductColumns(response.data.productColumns ?? []);
      setHasGenerated(true);
    } catch (err: any) {
      setError(err?.message ?? 'Não foi possível gerar o relatório. Tente novamente.');
      setRows([]);
      setPaginationState(null);
      setSummary(null);
      setProductColumns([]);
    } finally {
      setLoading(false);
    }
  }, []); // refs não precisam de deps — são atualizados a cada render

  // ---------------------------------------------------------------------------
  // API pública — configuração (NÃO disparam fetch)
  // ---------------------------------------------------------------------------

  const setDimension = useCallback(
    (dim: ReportDimension) => {
      dimensionRef.current = dim;
      setDimensionState(dim);
      setPageState(1);
      clearResult();
    },
    [clearResult]
  );

  const setDateStart = useCallback((date: string) => {
    dateStartRef.current = date;
    setDateStartState(date);
    setPageState(1);
  }, []);

  const setDateEnd = useCallback((date: string) => {
    dateEndRef.current = date;
    setDateEndState(date);
    setPageState(1);
  }, []);

  const setFilter = useCallback((key: keyof ReportFilters, value: string) => {
    setFiltersState((prev) => {
      const next = { ...prev, [key]: value };
      filtersRef.current = next;
      return next;
    });
    setPageState(1);
  }, []);

  const clearFilters = useCallback(() => {
    const empty = { ...EMPTY_FILTERS };
    filtersRef.current = empty;
    setFiltersState(empty);
    setPageState(1);
  }, []);

  // ---------------------------------------------------------------------------
  // API pública — fetch
  // ---------------------------------------------------------------------------

  /** Chama a callable com os parâmetros atuais. Usar quando o usuário clica "Gerar Relatório". */
  const generateReport = useCallback((): Promise<void> => {
    // page já terá sido resetado para 1 pelas mudanças de dimensão/filtros/período anteriores
    return doFetch(page, pageSizeRef.current);
  }, [doFetch, page]);

  // ---------------------------------------------------------------------------
  // API pública — paginação (DISPARAM fetch automaticamente)
  // ---------------------------------------------------------------------------

  /** Altera a página e dispara nova chamada à callable. */
  const setPage = useCallback(
    (newPage: number) => {
      setPageState(newPage);
      doFetch(newPage, pageSizeRef.current);
    },
    [doFetch]
  );

  /** Altera o tamanho da página, reseta para página 1 e dispara nova chamada à callable. */
  const setPageSize = useCallback(
    (newPageSize: number) => {
      pageSizeRef.current = newPageSize;
      setPageSizeState(newPageSize);
      setPageState(1);
      doFetch(1, newPageSize);
    },
    [doFetch]
  );

  // ---------------------------------------------------------------------------
  // API pública — exportação CSV
  // ---------------------------------------------------------------------------

  /**
   * Obtém idToken, chama downloadDeliveriesReportCsv (fetch POST com Bearer),
   * recebe Blob e dispara download. Gerencia estado `exporting`.
   */
  const exportCsv = useCallback(async (): Promise<void> => {
    const ds = dateStartRef.current;
    const de = dateEndRef.current;

    if (!ds || !de) {
      setError('Selecione um período válido antes de exportar');
      return;
    }

    setExporting(true);
    setError(null);

    try {
      const idToken = await auth.currentUser?.getIdToken();
      if (!idToken) throw new Error('Usuário não autenticado');

      const blob = await deliveriesReportService.downloadDeliveriesReportCsv(
        {
          dimension: dimensionRef.current,
          dateStart: ds,
          dateEnd: de,
          filters: buildFiltersParam(),
        },
        idToken
      );

      triggerBlobDownload(blob, buildCsvFilename(dimensionRef.current, ds, de));
    } catch (err: any) {
      setError(err?.message ?? 'Erro ao exportar CSV. Tente novamente.');
    } finally {
      setExporting(false);
    }
  }, []); // refs não precisam de deps

  return {
    dimension,
    dateStart,
    dateEnd,
    filters,
    page,
    pageSize,
    pagination,
    rows,
    summary,
    productColumns,
    loading,
    exporting,
    error,
    hasGenerated,
    setDimension,
    setDateStart,
    setDateEnd,
    setFilter,
    clearFilters,
    setPage,
    setPageSize,
    generateReport,
    exportCsv,
  };
}
